/* The Room — Matt's side of the conversation. The data half.
 *
 * FRAMEWORK-FREE, because this package is. The React panel lives in
 * @kilroy/room; everything here is fetch and types, so it can be imported by
 * anything and tested without a DOM.
 *
 * SAME ORIGIN, ALWAYS. Every call below is a relative path to the site's own
 * `/api/room/*` function, which holds the shared secret. There is no base URL
 * to configure, nothing cross-origin, and therefore no CORS to get wrong — the
 * single most common way this integration breaks somewhere else.
 *
 * NOTHING HERE THROWS, EVER. Two reasons, and the second is the serious one:
 *
 *   A question that errors has told somebody their question was unwelcome,
 *   which is the opposite of what the Room is for.
 *
 *   THIS RUNS INSIDE AN OFFLINE-FIRST APP WHOSE OUTBOX HOLDS THE ONLY COPY OF
 *   UNSENT EVIDENCE. Nothing here retries, queues, persists, or touches
 *   IndexedDB or OPFS. A chat panel that can wedge the sync path is a chat
 *   panel that can cost a geologist a day's work, so it is kept structurally
 *   incapable of it: plain fetch, no storage, failures swallowed.
 */

export const ROOM_COOKIE = 'kilroy_room';

/** Which app is asking. Both surfaces have a `/`, a `/priorities` and an
 *  `/evidence/:id`, so a bare pathname is ambiguous and would ground the twin
 *  on the wrong screen's record. Every lookup is prefixed. */
export type Surface = 'field' | 'desk';

export type Ask = {
  id: string;
  kind?: string;
  question: string;
  why?: string;
  options?: string[];
  surface?: string;
};

export type Screen = {
  path: string;
  what?: string;
  goal?: string;
  thinking?: string;
  ai?: string;
  assumed?: string[];
  ask?: string;
};

export type Room = {
  client?: string;
  build_name?: string;
  asks?: Ask[];
  screens?: Screen[];
  css?: string;
  /** Our mark, as inline SVG, sent with the payload so the tenant's app never
   *  has to carry a 33prime asset. */
  sun?: string;
  finale?: { head?: string; body?: string } | null;
};

export type Turn = { role: 'you' | 'twin'; text: string; refused?: boolean };

const api = (token: string, verb = '') =>
  `/api/room/${encodeURIComponent(token)}${verb ? `/${verb}` : ''}`;

const post = (token: string, verb: string, body: unknown, keepalive = false) =>
  fetch(api(token, verb), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive,
  });

/** The room for this token, or null. Null covers every failure — no room, bad
 *  token, upstream down, no signal — because the dock only needs one check and
 *  a stranger should learn nothing from the difference. */
export async function fetchRoom(token: string): Promise<Room | null> {
  try {
    const res = await fetch(api(token), { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    return data && typeof data === 'object' ? (data as Room) : null;
  } catch {
    return null;
  }
}

/** Claim a room token off the URL, if this load is an arrival.
 *
 * `/enter/<token>` → set the cookie, rewrite the URL to `/`, carry on.
 *
 * CALLED BEFORE REACT MOUNTS, from each app's entry. It has to be: the field
 * app puts a sign-in wall in front of every route, so a token arriving through
 * the router would meet that wall and be thrown away. This runs first and
 * leaves no trace in the address bar.
 *
 * The URL is replaced rather than pushed so the back button cannot walk
 * somebody back into a link they have already used.
 *
 * `path=/` here, unlike Holt's `/app`, because on these two sites the whole
 * origin IS the product — there is nothing else at the root to scope away from.
 */
export function claimRoomToken(): void {
  try {
    const m = /^\/enter\/([A-Za-z0-9_-]{8,64})\/?$/.exec(location.pathname);
    if (!m) return;
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    // 90 days: long enough that a client coming back next month is still known,
    // short enough that a shared laptop forgets eventually.
    document.cookie =
      `${ROOM_COOKIE}=${encodeURIComponent(m[1])}; path=/; max-age=7776000; SameSite=Lax${secure}`;
    history.replaceState(null, '', '/');
  } catch {
    /* a token that cannot be claimed just means no dock */
  }
}

export function readCookie(name: string): string {
  try {
    const hit = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : '';
  } catch {
    return '';
  }
}

/** The record for the screen someone is on.
 *
 * NEVER TRUST THE SHAPE. This payload comes from another service on another
 * deploy cycle. An older Room has no `screens` at all, and calling .find on
 * undefined would take the host app's layout down with it — the conversation
 * crashing the product it is a guest inside is the one thing this module must
 * never do. Missing field means no narration and a dock that still works.
 */
export function screenFor(room: Room | null, surface: Surface, pathname: string): Screen | null {
  const screens = room?.screens;
  if (!Array.isArray(screens)) return null;
  const full = `/${surface}${pathname === '/' ? '/' : pathname}`;
  const exact = screens.find((s) => s?.path === full);
  if (exact) return exact;
  // A dynamic route: /desk/evidence/abc must find /desk/evidence/[id] and NOT
  // fall back to /desk/evidence, which is a different screen with a different
  // story. Holt's dock fell back by prefix and narrated 8 screens wrongly.
  const got = full.split('/').filter(Boolean);
  const dyn = screens.find((s) => {
    const pat = String(s?.path ?? '').split('/').filter(Boolean);
    if (pat.length !== got.length) return false;
    return pat.every((p, i) => p.startsWith('[') || p === got[i]);
  });
  if (dyn) return dyn;
  return screens.find((s) => s?.path === `/${surface}/`) ?? null;
}

/** Ask the twin. Returns a turn either way — the failure case is a sentence a
 *  person can act on, not an empty bubble. */
export async function askTwin(
  token: string,
  question: string,
  surface: Surface,
  pathname: string,
  history: Turn[],
): Promise<Turn> {
  try {
    const res = await post(token, 'ask', {
      question,
      path: `/${surface}${pathname === '/' ? '/' : pathname}`,
      // Only enough to follow the thread. The corpus does the actual work.
      history: history.slice(-6).map((t) => ({ role: t.role, text: t.text })),
    });
    if (!res.ok) throw new Error(String(res.status));
    const d: { text?: unknown; refused?: unknown } = await res.json();
    const text = String(d?.text ?? '');
    if (!text) throw new Error('empty');
    return { role: 'twin', text, refused: Boolean(d?.refused) };
  } catch {
    return {
      role: 'twin',
      text: 'I could not reach my notes just then — worth trying again in a moment, or ask Matt directly.',
    };
  }
}

export async function sendAnswer(
  token: string,
  askId: string,
  text: string,
  skipped = false,
): Promise<boolean> {
  try {
    const res = await post(token, 'answer', { ask_id: askId, text, skipped });
    const d: { recorded?: unknown } = await res.json();
    return Boolean(d?.recorded);
  } catch {
    return false;
  }
}

/** Where they went and how long they stayed. OPERATOR-SIDE ONLY, FOREVER.
 *
 * The rule was never "do not count" — it was never mention the count to the
 * person being counted, and nothing anyone can see refers to any of this.
 *
 * `keepalive` because the interesting number is recorded as they LEAVE a
 * screen, and a normal fetch is cancelled by the navigation that triggers it.
 */
export function reportVisit(
  token: string,
  surface: Surface,
  pathname: string,
  seconds: number,
): void {
  try {
    void post(
      token,
      'visit',
      { path: `/${surface}${pathname === '/' ? '/' : pathname}`, seconds },
      true,
    ).catch(() => {});
  } catch {
    /* telemetry never breaks somebody's product */
  }
}
